import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { ConnectError, createClient } from '@connectrpc/connect';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AuthService } from 'proto/gen/ts/shirt/v1/auth_pb';
import { useState } from 'react';
import { TEST_IDS, wholesalerRow } from '../../utils/testIds';
import { transport } from '../../utils/transport';
const authClient = createClient(AuthService, transport);
const emptyForm = {
    email: '',
    password: '',
    companyName: '',
    taxId: '',
    approved: true
};
export default function Wholesalers() {
    const queryClient = useQueryClient();
    const [form, setForm] = useState(emptyForm);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const { data, isLoading } = useQuery({
        queryKey: [
            'wholesalers'
        ],
        queryFn: ()=>authClient.listWholesalers({})
    });
    const createMutation = useMutation({
        mutationFn: (input)=>authClient.createWholesaler({
                email: input.email.trim(),
                password: input.password,
                companyName: input.companyName.trim(),
                taxId: input.taxId.trim(),
                approved: input.approved
            }),
        onSuccess: (res)=>{
            setSuccess(`Created wholesaler ${res.wholesaler?.email}`);
            setError('');
            setForm(emptyForm);
            queryClient.invalidateQueries({
                queryKey: [
                    'wholesalers'
                ]
            });
        },
        onError: (err)=>{
            setSuccess('');
            setError(err instanceof ConnectError ? err.message : 'Failed to create wholesaler');
        }
    });
    const updateMutation = useMutation({
        mutationFn: (input)=>authClient.updateWholesaler({
                userId: input.userId,
                companyName: input.companyName,
                taxId: input.taxId,
                approved: input.approved
            }),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'wholesalers'
                ]
            });
        },
        onError: (err)=>{
            setError(err instanceof ConnectError ? err.message : 'Failed to update wholesaler');
        }
    });
    const deleteMutation = useMutation({
        mutationFn: (userId)=>authClient.deleteWholesaler({
                userId
            }),
        onSuccess: ()=>{
            queryClient.invalidateQueries({
                queryKey: [
                    'wholesalers'
                ]
            });
        },
        onError: (err)=>{
            setError(err instanceof ConnectError ? err.message : 'Failed to delete wholesaler');
        }
    });
    const handleCreate = (e)=>{
        e.preventDefault();
        setError('');
        setSuccess('');
        createMutation.mutate(form);
    };
    const wholesalers = data?.wholesalers ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8",
        children: [
            /*#__PURE__*/ _jsx("h1", {
                className: "font-heading text-2xl font-bold text-stone-900 mb-6",
                children: "Wholesalers"
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: "bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm mb-4",
                children: error
            }),
            success && /*#__PURE__*/ _jsx("div", {
                className: "bg-green-50 border border-green-200 text-green-700 px-4 py-3 text-sm mb-4",
                children: success
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border-2 border-stone-100 p-6 mb-8",
                children: [
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-sm font-semibold uppercase tracking-wider text-stone-900 mb-4",
                        children: "Invite wholesaler"
                    }),
                    /*#__PURE__*/ _jsxs("form", {
                        onSubmit: handleCreate,
                        className: "grid grid-cols-1 sm:grid-cols-2 gap-4",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "ws-email",
                                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                        children: "Email"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "ws-email",
                                        "data-testid": TEST_IDS.wholesalerEmailInput,
                                        type: "email",
                                        value: form.email,
                                        onChange: (e)=>setForm({
                                                ...form,
                                                email: e.target.value
                                            }),
                                        required: true,
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "ws-company",
                                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                        children: "Company name"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "ws-company",
                                        "data-testid": TEST_IDS.wholesalerCompanyInput,
                                        type: "text",
                                        value: form.companyName,
                                        onChange: (e)=>setForm({
                                                ...form,
                                                companyName: e.target.value
                                            }),
                                        required: true,
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "ws-tax",
                                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                        children: "Tax ID (optional)"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "ws-tax",
                                        type: "text",
                                        value: form.taxId,
                                        onChange: (e)=>setForm({
                                                ...form,
                                                taxId: e.target.value
                                            }),
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("label", {
                                        htmlFor: "ws-password",
                                        className: "block text-xs font-semibold uppercase tracking-wider text-stone-700 mb-1",
                                        children: "Initial password"
                                    }),
                                    /*#__PURE__*/ _jsx("input", {
                                        id: "ws-password",
                                        "data-testid": TEST_IDS.wholesalerPasswordInput,
                                        type: "text",
                                        value: form.password,
                                        onChange: (e)=>setForm({
                                                ...form,
                                                password: e.target.value
                                            }),
                                        required: true,
                                        minLength: 6,
                                        placeholder: "Share with them out-of-band",
                                        className: "w-full border-2 border-stone-200 px-3 py-2 text-sm focus:border-stone-900 focus:outline-none font-mono"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "sm:col-span-2 flex items-center justify-between",
                                children: [
                                    /*#__PURE__*/ _jsxs("label", {
                                        className: "flex items-center gap-2 cursor-pointer",
                                        children: [
                                            /*#__PURE__*/ _jsx("input", {
                                                type: "checkbox",
                                                "data-testid": TEST_IDS.wholesalerApprovedCheckbox,
                                                checked: form.approved,
                                                onChange: (e)=>setForm({
                                                        ...form,
                                                        approved: e.target.checked
                                                    })
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-xs font-semibold uppercase tracking-wider text-stone-700",
                                                children: "Approved (can place wholesale orders)"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        type: "submit",
                                        "data-testid": TEST_IDS.wholesalerCreateButton,
                                        disabled: createMutation.isPending,
                                        className: "px-6 py-3 bg-stone-900 text-white text-xs font-semibold uppercase tracking-wider hover:bg-stone-700 disabled:opacity-60",
                                        children: createMutation.isPending ? 'Creating...' : 'Create wholesaler'
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("h2", {
                className: "text-sm font-semibold uppercase tracking-wider text-stone-900 mb-3",
                children: [
                    "All wholesalers (",
                    wholesalers.length,
                    ")"
                ]
            }),
            isLoading ? /*#__PURE__*/ _jsx("p", {
                className: "text-stone-500",
                children: "Loading..."
            }) : wholesalers.length === 0 ? /*#__PURE__*/ _jsx("p", {
                className: "text-stone-500",
                children: "No wholesalers yet."
            }) : /*#__PURE__*/ _jsx("div", {
                className: "bg-white border-2 border-stone-100 divide-y divide-stone-100",
                children: wholesalers.map((w)=>/*#__PURE__*/ _jsx(WholesalerRow, {
                        userId: w.userId,
                        email: w.email,
                        companyName: w.companyName,
                        taxId: w.taxId,
                        isApproved: w.isApproved,
                        approvedAt: w.approvedAt,
                        onSave: (fields)=>updateMutation.mutate({
                                userId: w.userId,
                                ...fields
                            }),
                        onDelete: ()=>{
                            if (window.confirm(`Delete wholesaler ${w.email}?`)) {
                                deleteMutation.mutate(w.userId);
                            }
                        },
                        saving: updateMutation.isPending
                    }, w.userId))
            })
        ]
    });
}
function WholesalerRow(props) {
    const [editing, setEditing] = useState(false);
    const [companyName, setCompanyName] = useState(props.companyName);
    const [taxId, setTaxId] = useState(props.taxId);
    const [approved, setApproved] = useState(props.isApproved);
    const handleSave = ()=>{
        props.onSave({
            companyName,
            taxId,
            approved
        });
        setEditing(false);
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": wholesalerRow(props.email),
        className: "p-4 flex items-center gap-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex-1 min-w-0",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm font-semibold text-stone-900 truncate",
                                children: props.email
                            }),
                            props.isApproved ? /*#__PURE__*/ _jsx("span", {
                                className: "text-[10px] uppercase tracking-wider bg-green-100 text-green-700 px-1.5 py-0.5",
                                children: "Approved"
                            }) : /*#__PURE__*/ _jsx("span", {
                                className: "text-[10px] uppercase tracking-wider bg-yellow-100 text-yellow-700 px-1.5 py-0.5",
                                children: "Pending"
                            })
                        ]
                    }),
                    editing ? /*#__PURE__*/ _jsxs("div", {
                        className: "mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("input", {
                                type: "text",
                                value: companyName,
                                onChange: (e)=>setCompanyName(e.target.value),
                                placeholder: "Company",
                                className: "border-2 border-stone-200 px-2 py-1 text-xs"
                            }),
                            /*#__PURE__*/ _jsx("input", {
                                type: "text",
                                value: taxId,
                                onChange: (e)=>setTaxId(e.target.value),
                                placeholder: "Tax ID",
                                className: "border-2 border-stone-200 px-2 py-1 text-xs"
                            }),
                            /*#__PURE__*/ _jsxs("label", {
                                className: "flex items-center gap-2 text-xs",
                                children: [
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "checkbox",
                                        checked: approved,
                                        onChange: (e)=>setApproved(e.target.checked)
                                    }),
                                    "Approved"
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-stone-500 mt-1",
                        children: [
                            props.companyName,
                            props.taxId && ` · ${props.taxId}`
                        ]
                    })
                ]
            }),
            editing ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: handleSave,
                        disabled: props.saving,
                        className: "text-xs font-semibold uppercase tracking-wider text-stone-900 underline",
                        children: "Save"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>{
                            setCompanyName(props.companyName);
                            setTaxId(props.taxId);
                            setApproved(props.isApproved);
                            setEditing(false);
                        },
                        className: "text-xs text-stone-500 hover:text-stone-900",
                        children: "Cancel"
                    })
                ]
            }) : /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setEditing(true),
                        className: "text-xs font-semibold uppercase tracking-wider text-stone-700 hover:text-stone-900 underline",
                        children: "Edit"
                    }),
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: props.onDelete,
                        className: "text-xs text-red-600 hover:text-red-800",
                        children: "Delete"
                    })
                ]
            })
        ]
    });
}
